// Shared "enter your 6-digit code" step used by LoginPage (password + Google
// paths), SetPasswordPage (magic-link redeem), and AdminLoginPage. Rendered
// in place of the normal form once the server has responded `mfa_required`.
// Exchanges the mfa_token for a real session via useAuth().verifyMfa.
//
// Three code sources feed the same input: the authenticator app (default),
// an unused recovery code, or a 6-digit code emailed on request (the
// "Email me a code instead" fallback) — exactly one is submitted per attempt.
import * as React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useAuth, type Identity } from './AuthProvider';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { api, ApiError } from '../api-client';

const EMAIL_RESEND_COOLDOWN_SECONDS = 30;

type Step = 'totp' | 'recovery' | 'email';

export function MfaChallengeStep({
  mfaToken,
  onSuccess,
  onBack,
}: {
  mfaToken: string;
  onSuccess: (identity: Identity) => void;
  onBack: () => void;
}) {
  const { verifyMfa } = useAuth();
  const [step, setStep] = React.useState<Step>('totp');
  const [code, setCode] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [emailSending, setEmailSending] = React.useState(false);
  const [cooldown, setCooldown] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => { inputRef.current?.focus(); }, [step]);

  React.useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setInterval(() => {
      setCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => window.clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cooldown > 0]);

  async function sendEmailCode() {
    setError(null);
    setEmailSending(true);
    try {
      await api('/api/auth/mfa/email-code', { method: 'POST', body: { mfa_token: mfaToken } });
      setStep('email');
      setCode('');
      setCooldown(EMAIL_RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'rate_limited') {
        setError('Too many attempts — please wait a few minutes.');
      } else {
        setError('Could not send the code — please try again.');
      }
    } finally {
      setEmailSending(false);
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!code.trim()) return;
    setSubmitting(true);
    try {
      const identity = await verifyMfa(
        step === 'recovery'
          ? { mfaToken, recoveryCode: code.trim() }
          : step === 'email'
          ? { mfaToken, emailCode: code.trim() }
          : { mfaToken, code: code.trim() },
      );
      onSuccess(identity);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'rate_limited') setError('Too many attempts — please wait a few minutes.');
        else if (err.code === 'invalid_mfa_token') setError('This sign-in attempt expired — please sign in again.');
        else setError(err.message);
      } else {
        setError(String((err as any)?.message ?? err));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <p className="text-[13.5px] font-semibold text-ink/80 dark:text-white/80">
          Two-factor authentication
        </p>
        <p className="text-[12.5px] text-ink/55 dark:text-white/55 mt-1">
          {step === 'recovery'
            ? 'Enter one of your unused recovery codes.'
            : step === 'email'
            ? 'Code sent to your email — enter it below.'
            : 'Enter the 6-digit code from your authenticator app.'}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="mfa-code" className="text-[12px] font-semibold text-ink/70 dark:text-white/70">
          {step === 'recovery' ? 'Recovery code' : step === 'email' ? 'Email code' : 'Authentication code'}
        </Label>
        <Input
          id="mfa-code"
          ref={inputRef}
          type="text"
          inputMode={step === 'recovery' ? 'text' : 'numeric'}
          autoComplete="one-time-code"
          autoFocus
          maxLength={step === 'recovery' ? 12 : 6}
          placeholder={step === 'recovery' ? 'XXXXXXXXXX' : '000000'}
          value={code}
          onChange={(e) => setCode(e.target.value)}
          className="tracking-[0.2em] text-center font-mono"
        />
      </div>

      {step === 'email' && (
        <button
          type="button"
          onClick={sendEmailCode}
          disabled={emailSending || cooldown > 0}
          className="w-full text-center text-[12px] font-semibold text-ink/50 dark:text-white/50 hover:text-ink dark:hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:text-ink/50 dark:disabled:hover:text-white/50"
        >
          {cooldown > 0 ? `Resend code in ${cooldown}s` : emailSending ? 'Sending…' : 'Resend code'}
        </button>
      )}

      {error && (
        <div className="text-[13px] text-rose-700 dark:text-rose-300 bg-rose-500/10 ring-1 ring-rose-500/20 rounded-2xl px-3 py-2" role="alert">
          {error}
        </div>
      )}

      <Button type="submit" size="lg" className="w-full" disabled={submitting || !code.trim()}>
        {submitting ? 'Verifying…' : 'Verify'}
      </Button>

      {step !== 'email' && (
        <button
          type="button"
          onClick={() => { setStep((s) => (s === 'recovery' ? 'totp' : 'recovery')); setCode(''); setError(null); }}
          className="w-full text-center text-[12.5px] font-semibold text-ink/55 dark:text-white/55 hover:text-ink dark:hover:text-white transition-colors"
        >
          {step === 'recovery' ? 'Use your authenticator app instead' : 'Use a recovery code instead'}
        </button>
      )}

      {step === 'totp' && (
        <button
          type="button"
          onClick={sendEmailCode}
          disabled={emailSending}
          className="w-full text-center text-[12.5px] font-semibold text-ink/55 dark:text-white/55 hover:text-ink dark:hover:text-white transition-colors disabled:opacity-50"
        >
          {emailSending ? 'Sending…' : 'Email me a code instead'}
        </button>
      )}

      {step === 'email' && (
        <button
          type="button"
          onClick={() => { setStep('totp'); setCode(''); setError(null); }}
          className="w-full text-center text-[12.5px] font-semibold text-ink/55 dark:text-white/55 hover:text-ink dark:hover:text-white transition-colors"
        >
          Use your authenticator app instead
        </button>
      )}

      <button
        type="button"
        onClick={onBack}
        className="w-full inline-flex items-center justify-center gap-1.5 text-[12.5px] font-semibold text-ink/50 dark:text-white/50 hover:text-ink dark:hover:text-white transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back to sign in
      </button>
    </form>
  );
}
